import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { installerBranding, InstalledByInstaller } from "@/branding/Branding";
import ActivityFeed from "@/components/ActivityFeed";
import HomeScene from "@/components/customer/HomeScene";
import JourneyRail from "@/components/customer/JourneyRail";
import NextVisitCard from "@/components/customer/NextVisitCard";
import SystemPreview from "@/components/customer/SystemPreview";
import DocumentsPreview from "@/components/customer/DocumentsPreview";
import ActionsCard from "@/components/customer/ActionsCard";
import SupportCard from "@/components/customer/SupportCard";
import { projectLifecycle } from "@/lib/journeyGroups";
import { statusHeadline } from "@/lib/customerFormat";
import { projectCompletionField, projectNextMilestone, projectStageLabel } from "@/lib/projectPresentation";
import { AlertTriangle, Sparkles, RefreshCw } from "lucide-react";

/**
 * CUST-EXP-001A1 · Customer Command Centre (corrected)
 * ------------------------------------------------------------------
 * Fixes over CUST-EXP-001A:
 *  • Hero and status surface use a proper `isolate` stacking context
 *    so the intended dark gradient reliably paints behind white text
 *    (previously masked by CustomerShell's `bg-[#F5F5F7]` root).
 *  • Greeting punctuation is deterministic — no stray whitespace.
 *  • Estimated completion never shows a past label as a forward-looking
 *    promise; falsy / past values collapse to "Being reviewed".
 *  • Reassurance copy is evidence-based (`statusHeadline`) — no
 *    unqualified "everything is on track".
 *  • Wider desktop widths get a slightly larger max-width so the
 *    content is centred instead of hugging the left edge.
 */

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
}

function displayFirstName(user) {
    const name = (user?.name || "").trim();
    if (name) return name.split(/\s+/)[0];
    const email = (user?.email || "").trim();
    if (email) return email.split("@")[0];
    return "there";
}

export default function Dashboard() {
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [projectLoaded, setProjectLoaded] = useState(false);
    const [projectError, setProjectError] = useState(false);
    const [nextAppt, setNextAppt] = useState(null);
    const [equipment, setEquipment] = useState([]);
    const [equipmentLoading, setEquipmentLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [unreadMessages, setUnreadMessages] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = await api.myProject();
                if (cancelled) return;
                setProject(p);
                setProjectError(false);
            } catch (e) {
                if (cancelled) return;
                setProject(null);
                setProjectError(e?.response?.status !== 404);
            } finally {
                if (!cancelled) setProjectLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!project?.id) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const appts = await api.listAppointments(project.id);
                if (cancelled) return;
                const nowIso = new Date().toISOString();
                const next = (appts || [])
                    .filter((a) => a.scheduled_at && a.scheduled_at >= nowIso && a.status !== "cancelled")
                    .sort((a, b) => (a.scheduled_at > b.scheduled_at ? 1 : -1))[0] || null;
                setNextAppt(next);
            } catch { if (!cancelled) setNextAppt(null); }
        })();
        (async () => {
            try {
                setEquipmentLoading(true);
                const resp = await api.listEquipment(project.id);
                if (cancelled) return;
                const items = Array.isArray(resp) ? resp : (resp?.equipment || []);
                setEquipment(items);
            } catch { if (!cancelled) setEquipment([]); }
            finally { if (!cancelled) setEquipmentLoading(false); }
        })();
        (async () => {
            try {
                setDocumentsLoading(true);
                const docs = await api.listDocuments(project.id);
                if (!cancelled) setDocuments(docs || []);
            } catch { if (!cancelled) setDocuments([]); }
            finally { if (!cancelled) setDocumentsLoading(false); }
        })();
        (async () => {
            try {
                const resp = await api.projectMessagesUnreadCount(project.id);
                if (!cancelled) setUnreadMessages(resp?.unread_count || 0);
            } catch { if (!cancelled) setUnreadMessages(0); }
        })();
        return () => { cancelled = true; };
    }, [project?.id]);

    const firstName = displayFirstName(user);
    const lifecycle = useMemo(() => projectLifecycle(project), [project]);
    const status = useMemo(() => statusHeadline(project), [project]);
    const completion = useMemo(() => projectCompletionField(project), [project]);

    // ── render states ─────────────────────────────────────────────

    if (!projectLoaded) {
        return (
            <div className="min-h-full flex items-center justify-center" data-testid="dashboard-loading"
                 style={{ background: "linear-gradient(180deg,#0A1226 0%,#101F3F 100%)" }}>
                <div className="text-white/70 text-sm inline-flex items-center gap-2">
                    <RefreshCw size={14} className="animate-spin motion-reduce:animate-none" />
                    Loading your Command Centre…
                </div>
            </div>
        );
    }

    if (projectError) {
        return (
            <div className="min-h-full p-6 md:p-10" data-testid="dashboard-error"
                 style={{ background: "linear-gradient(180deg,#0A1226 0%,#101F3F 100%)" }}>
                <div className="max-w-md mx-auto pe-card p-6 mt-16 text-center">
                    <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                    <div className="mt-3 text-lg text-[#131B2F] font-medium">We couldn't reach your project</div>
                    <p className="mt-1 text-sm text-[#4B5565]">Please try again in a moment — your data is safe.</p>
                    <button
                        onClick={() => window.location.reload()}
                        data-testid="dashboard-error-retry"
                        className="mt-4 pe-btn-primary text-sm h-10"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-full" data-testid="dashboard-page"
                 style={{ background: "linear-gradient(180deg,#0A1226 0%,#101F3F 55%,#1D2E5C 100%)" }}>
                <div className="max-w-4xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 text-center">
                    <Sparkles size={28} className="mx-auto text-[#22B8CF]" />
                    <h1 className="mt-4 text-3xl md:text-4xl text-white font-medium">Welcome to Pearl EnergyFlow</h1>
                    <p className="mt-2 text-white/80 max-w-md mx-auto">
                        Your project isn't set up yet. Your installer will get started shortly — you'll see your
                        journey, documents and system come to life here.
                    </p>
                    <div className="mt-6"><InstalledByInstaller className="justify-center inline-flex" /></div>
                </div>
            </div>
        );
    }

    // ── main render ──────────────────────────────────────────────

    // Hero surface — explicit isolated stacking context. This is the
    // fix for the contrast regression: previously an `-z-10` inner
    // gradient was painted BEHIND CustomerShell's `bg-[#F5F5F7]` root,
    // leaving white hero text over a pale surface.
    const heroBg = lifecycle === "commissioned"
        ? "linear-gradient(180deg,#0A1226 0%,#101F3F 35%,#1B3B76 70%,#22B8CF 100%)"
        : "linear-gradient(180deg,#0A1226 0%,#101F3F 45%,#1D2E5C 100%)";

    return (
        <div
            className="min-h-full customer-command-centre"
            data-testid="dashboard-page"
            data-lifecycle={lifecycle}
        >
            {/* HERO --------------------------------------------------- */}
            <section
                className="relative isolate customer-command-centre__hero"
                data-testid="hero-surface"
                style={{ background: heroBg }}
            >
                <div
                    className="absolute inset-0 pointer-events-none"
                    aria-hidden="true"
                    style={{
                        background:
                            "radial-gradient(circle at 15% 0%, rgba(59,130,246,0.35) 0%, transparent 55%)," +
                            "radial-gradient(circle at 90% 100%, rgba(124,58,237,0.28) 0%, transparent 60%)",
                    }}
                />

                <div className="relative mx-auto max-w-6xl 2xl:max-w-7xl px-4 md:px-8 pt-6 md:pt-10 pb-10 md:pb-14">
                    <div className="flex items-start justify-between gap-4 pb-6">
                        <div className="min-w-0">
                            <div className="text-white/75 text-[11px] tracking-[0.16em] uppercase">
                                {installerBranding.name} · Customer Command Centre
                            </div>
                            <h1 className="mt-1 text-3xl md:text-4xl lg:text-5xl text-white font-medium">
                                {/* NOTE: single template avoids stray JSX whitespace before the comma. */}
                                {`${greeting()}, `}
                                <span data-testid="hero-name">{firstName}</span>
                                {"."}
                            </h1>
                            <p className="mt-2 text-white/85 max-w-xl" data-testid="hero-status">
                                {status.line}
                            </p>
                        </div>
                        {/* CUST-EXP-001A2 · The approved Pearl wordmark is
                            full-colour black. To keep it legible on the
                            navy hero — and without recolouring the
                            approved artwork — we wrap the untouched logo
                            in a compact white badge with a subtle border
                            and shadow. Kept visually secondary to the
                            greeting. */}
                        <div
                            className="hidden md:flex items-center justify-center bg-white rounded-xl px-3 py-2 shrink-0 border border-white/30 shadow-[0_10px_30px_-15px_rgba(4,10,25,0.55)]"
                            data-testid="hero-brand-badge"
                        >
                            <img
                                src={installerBranding.logoUrl}
                                alt={`${installerBranding.name} logo`}
                                className="h-9 lg:h-10 w-auto object-contain"
                                data-testid="hero-brand-logo"
                            />
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-5 gap-5 md:gap-6">
                        <div className="lg:col-span-3">
                            <HomeScene
                                project={project}
                                equipmentItems={equipment}
                                lifecycle={lifecycle}
                                progress={project.progress}
                            />
                        </div>

                        <aside className="lg:col-span-2 flex flex-col gap-4">
                            <div
                                className="rounded-[20px] backdrop-blur-md bg-white/[0.08] border border-white/25 p-5 md:p-6 text-white shadow-[0_20px_40px_-20px_rgba(0,0,0,0.55)]"
                                data-testid="hero-status-card"
                            >
                                <div className="text-[11px] tracking-[0.16em] uppercase text-white/70">Current phase</div>
                                <div className="mt-1 text-xl md:text-2xl font-medium" data-testid="hero-current-phase">
                                    {projectStageLabel(project)}
                                </div>
                                <div className="mt-3 flex items-center gap-3">
                                    <div className="flex-1 h-1.5 rounded-full bg-white/15 overflow-hidden">
                                        <div
                                            className="h-full transition-all duration-700 motion-reduce:transition-none"
                                            style={{
                                                width: `${project.progress ?? 0}%`,
                                                background: lifecycle === "commissioned" ? "#3E9E2E" : "#22B8CF",
                                            }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium" data-testid="hero-progress">
                                        {project.progress ?? 0}%
                                    </span>
                                </div>
                                <div className="mt-4 space-y-1 text-sm">
                                    <div className="text-white/70">Next milestone</div>
                                    <div className="text-white font-medium" data-testid="hero-next-milestone">
                                        {project.next_milestone}
                                    </div>
                                </div>
                                <div className="mt-3 text-sm">
                                    {lifecycle === "commissioned" ? (
                                        <>
                                            <div className="text-white/70">System status</div>
                                            <div className="text-white font-medium" data-testid="hero-system-status">
                                                Commissioned
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-white/70" data-testid="hero-completion-label">{completion.label}</div>
                                            <div className="text-white font-medium" data-testid="hero-est-completion" data-tone={completion.tone}>
                                                {completion.value}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <Link
                                    to="/journey"
                                    data-testid="hero-open-journey"
                                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#22B8CF] hover:text-white transition-colors"
                                >
                                    Open your journey →
                                </Link>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* LOWER CONTENT ----------------------------------------- */}
            <div className="bg-[#F5F5F7] pb-16">
                <div className="mx-auto max-w-6xl 2xl:max-w-7xl px-4 md:px-8 pt-8 md:pt-10 space-y-5 md:space-y-6">
                    <JourneyRail project={project} />

                    <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                        <ActionsCard unreadMessages={unreadMessages} />
                        <NextVisitCard appt={nextAppt} lifecycle={lifecycle} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                        <SystemPreview items={equipment} loading={equipmentLoading} />
                        <DocumentsPreview docs={documents} loading={documentsLoading} />
                    </div>

                    <div className="pe-card p-5 md:p-6" data-testid="energy-honesty">
                        <div className="flex items-start gap-3">
                            <span className="pe-icon-tile w-8 h-8"><Sparkles size={14} strokeWidth={1.75} /></span>
                            <div className="min-w-0">
                                <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565]">Energy overview</div>
                                <div className="mt-1 text-base text-[#131B2F] font-medium">
                                    Energy monitoring is not connected yet.
                                </div>
                                {/* CUST-EXP-001A3 · Conditional truthful copy —
                                    a sparse customer with no equipment and no
                                    documents must not be told those things are
                                    "available below". */}
                                <p className="mt-1 text-sm text-[#4B5565] max-w-xl" data-testid="energy-honesty-copy">
                                    {(equipment.length === 0 && documents.length === 0)
                                        ? "Energy monitoring and system information will appear here after your equipment is installed and recorded."
                                        : "We haven't linked live generation, consumption or savings data for your home yet. Your installed equipment and documentation are available below — we'll add live figures once your monitoring is set up."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                        <div data-testid="recent-updates">
                            <ActivityFeed
                                variant="customer"
                                projectId={project.id}
                                initialLimit={5}
                                testidPrefix="dashboard-activity"
                            />
                        </div>
                        <SupportCard />
                    </div>

                    <div className="flex items-center justify-center pt-2">
                        <InstalledByInstaller />
                    </div>
                </div>
            </div>
        </div>
    );
}
